"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AiAgentsSubpageCopy } from "./ai-agents-page-copy";
import { CcAgentHowView } from "./CcAgentHowViews";

type CcAgentHowProps = {
  copy: AiAgentsSubpageCopy["how"];
};

/**
 * "How an AI agent works" after twin.so's numbered walkthrough: the steps
 * scroll past while a pinned panel swaps to the matching product view.
 * On phones each step shows its view inline instead.
 */
export function CcAgentHow({ copy }: CcAgentHowProps) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(Number((entry.target as HTMLElement).dataset.index));
          }
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const step of stepRefs.current) {
      if (step) observer.observe(step);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="cc-section cc-agent-how"
      id="ai-agent-how"
      aria-labelledby="cc-agent-how-title"
      data-cc-nav="light"
      {...devSectionAttr("ai-agent-how")}
    >
      <div className="cc-agent-how__container">
        <header className="cc-agent-how__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-agent-how-title" className="cc-agent-how__title">
            {copy.title}
          </h2>
          <p className="cc-agent-how__intro">{copy.intro}</p>
        </header>

        <div className="cc-agent-how__layout">
          <ol className="cc-agent-how__steps">
            {copy.steps.map((step, index) => (
              <li
                key={step.id}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-index={index}
                data-active={index === active ? "true" : undefined}
                className="cc-agent-how__step"
              >
                <span className="cc-agent-how__num">{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                <div className="cc-agent-how__inline" aria-hidden="true">
                  <CcAgentHowView id={step.id} ui={copy.ui} />
                </div>
              </li>
            ))}
            <li className="cc-agent-how__cta">
              <CcButton bookCallPopup bookCallVariant="ai-agents" label={copy.cta} />
              <small>{copy.ctaNote}</small>
            </li>
          </ol>

          <div className="cc-agent-how__visual" aria-hidden="true">
            <div className="cc-agent-how__sticky">
              <div className="cc-agent-how__stage">
                {copy.steps.map((step, index) => (
                  <div
                    key={step.id}
                    className="cc-agent-how__panel"
                    data-active={index === active ? "true" : undefined}
                    style={{ "--cc-ag-panel": index } as CSSProperties}
                  >
                    <CcAgentHowView id={step.id} ui={copy.ui} />
                  </div>
                ))}
              </div>
              <span className="cc-agent-how__progress">
                {copy.steps.map((step, index) => (
                  <i key={step.id} data-done={index <= active ? "true" : undefined} />
                ))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
